import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface PaginationProps {
  pagination: PaginationMeta
  baseUrl: string
  params?: Record<string, string>
  pageParam?: string
}

export default function Pagination({ pagination, baseUrl, params = {}, pageParam = "page" }: PaginationProps) {
  if (pagination.pages <= 1) return null

  function navigate(page: number) {
    router.get(baseUrl, { ...params, [pageParam]: page }, { preserveState: true, preserveScroll: true })
  }

  return (
    <div className="flex items-center justify-between pt-2">
      <p className="text-sm text-muted-foreground tabular-nums">
        {pagination.from}&ndash;{pagination.to} of {pagination.count}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          disabled={!pagination.prev}
          onClick={() => pagination.prev && navigate(pagination.prev)}
        >
          <ChevronLeftIcon className="size-4" />
          Prev
        </Button>
        {pageNumbers(pagination.page, pagination.pages).map((p, i) =>
          p === null ? (
            <span key={`ellipsis-${i}`} className="px-1 text-xs text-muted-foreground select-none">
              &hellip;
            </span>
          ) : (
            <Button
              key={p}
              variant={p === pagination.page ? "secondary" : "ghost"}
              size="icon-sm"
              onClick={() => navigate(p)}
            >
              {p}
            </Button>
          ),
        )}
        <Button
          variant="ghost"
          size="sm"
          disabled={!pagination.next}
          onClick={() => pagination.next && navigate(pagination.next)}
        >
          Next
          <ChevronRightIcon className="size-4" />
        </Button>
      </div>
    </div>
  )
}

function pageNumbers(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: (number | null)[] = [1]

  if (current > 3) pages.push(null)

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  for (let i = start; i <= end; i++) pages.push(i)

  if (current < total - 2) pages.push(null)

  pages.push(total)
  return pages
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
    </svg>
  )
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  )
}
